// Shared definition of "does this transcript have anything to display".
//
// Lives in its own module rather than on CallTranscript so that callers deciding
// whether to render CallTranscript at all can import it without importing the
// component: the Voice slide-out tests mock the CallTranscript module with only a
// default export, so a named export there makes any importer order-dependent
// (`Export named ... not found` when a sibling test's mock wins first).
//
// A transcript reaches these helpers in one of several shapes, all of which mean
// "nothing to show" and must agree with what CallTranscript actually renders:
//
//   * null / undefined / ''            -- never populated
//   * NO_TRANSCRIPTION / []            -- populated, but empty
//   * [{ role: 'tool', ... }]          -- populated, but every turn is filtered
//   * [{ role: 'user', content: '' }]  -- populated, but no turn carries content
//
// The last two are why a length check is not enough: the renderer drops tool
// calls and content-less turns, so a transcript can be non-empty and still
// render a blank panel.
export const NO_TRANSCRIPTION = '[]';

// Normalizes either shape the value can arrive in -- the raw JSON-column value
// (already an array by the time it crosses the API boundary) or a string some
// caller re-encoded -- to an array of turns.
//
// Returns null, distinctly from [], when the value will not parse or is not an
// array. That is not "no transcript": CallTranscript renders an explicit
// "Error parsing transcript" message for it, so callers must be able to tell a
// malformed transcript (which has something to show) from an empty one.
export const parseTranscriptItems = (transcript) => {
  if (!transcript || transcript === NO_TRANSCRIPTION) return [];

  try {
    const parsed = typeof transcript === 'string' ? JSON.parse(transcript || '[]') : transcript;
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

// The turns CallTranscript actually paints. Tool calls are internal plumbing and
// a turn with no content has nothing to say, so neither reaches the DOM.
export const displayableTranscriptItems = (items) => (
  (items || []).filter((item) => item?.role !== 'tool' && item?.content)
);

// True when CallTranscript would render something other than its empty state --
// either at least one displayable turn, or the parse-error message. Callers use
// this to decide whether the transcript section is worth showing at all.
export const hasTranscriptContent = (transcript) => {
  const items = parseTranscriptItems(transcript);
  // Malformed: the renderer surfaces the error rather than a blank panel, and
  // silently hiding a corrupt transcript would leave no trace of the corruption.
  if (items === null) return true;

  return displayableTranscriptItems(items).length > 0;
};
